import React, { useMemo } from "react";
import type { MatchStatus } from "service";
import MatchTimeline, {
  MatchTimelineEvent,
} from "components/matchTimeline";
import { getTeamColors } from "components/colors";

const normalizeName = (value?: string | null) =>
  value
    ? value
        .replace(/\(.*?\)/g, "")
        .trim()
        .toLowerCase()
    : undefined;

const detectTeam = (
  candidate: string,
  homeNames: string[],
  awayNames: string[]
): "home" | "away" | undefined => {
  const lc = candidate.toLowerCase();
  if (homeNames.some((name) => name && lc.includes(name))) return "home";
  if (awayNames.some((name) => name && lc.includes(name))) return "away";
  return undefined;
};

const extractMinuteParts = (
  label: string
): { minute?: number; extra?: number } => {
  const match = label.match(/(\d+)(?:\+(\d+))?'/);
  if (!match) return {};
  const minute = Number(match[1]);
  const extra = match[2] ? Number(match[2]) : undefined;
  return { minute, extra };
};

const periodLabelFromText = (text: string): string | undefined => {
  const lower = text.toLowerCase();
  if (lower.includes("1st period")) return "P1";
  if (lower.includes("2nd period")) return "P2";
  if (lower.includes("3rd period")) return "P3";
  if (lower.includes("ot") || lower.includes("overtime")) return "OT";
  if (lower.includes("shootout")) return "SO";
  return undefined;
};

const deriveTypeFromText = (text: string): string => {
  const lower = text.toLowerCase();
  if (lower.includes("goal")) return "goal";
  if (lower.includes("penalty shot")) return "penalty_shot";
  if (lower.includes("penalty")) return "penalty";
  if (lower.includes("power play")) return "power_play";
  if (lower.includes("timeout")) return "timeout";
  if (lower.includes("face-off") || lower.includes("faceoff"))
    return "faceoff";
  if (lower.includes("shot")) return "shot";
  if (lower.includes("whistle") || lower.includes("puck dropped"))
    return "whistle";
  return "generic";
};

export const buildIceHockeyMatchTimelineEvents = (
  status: MatchStatus
): MatchTimelineEvent[] => {
  const rawEvents = (status as any)?.m;
  if (!Array.isArray(rawEvents)) return [];

  const homeNames = [
    normalizeName((status as any)?.T1?.n),
    normalizeName((status as any)?.t1?.n),
    normalizeName((status as any)?.T1n),
  ].filter(Boolean) as string[];

  const awayNames = [
    normalizeName((status as any)?.T2?.n),
    normalizeName((status as any)?.t2?.n),
    normalizeName((status as any)?.T2n),
  ].filter(Boolean) as string[];

  const events = rawEvents
    .map((item: any, index: number): MatchTimelineEvent | null => {
      const rawLabel = typeof item?.n === "string" ? item.n.trim() : "";
      if (!rawLabel) return null;

      const lowerLabel = rawLabel.toLowerCase();
      const { minute: minuteFromText, extra } = extractMinuteParts(rawLabel);

      const tmValue = Number(
        typeof item?.tm === "number"
          ? item.tm
          : typeof item?.tm === "string"
          ? item.tm
          : NaN
      );
      const minute = Number.isFinite(tmValue)
        ? Math.max(0, Math.floor(tmValue / 60))
        : minuteFromText ?? 0;

      const sections = rawLabel
        .split(" - ")
        .map((part: string) => part.trim())
        .filter((part: string) => part.length > 0);

      const partsWithoutMinute = sections.filter(
        (part: string) => !/^\d+(?:\+\d+)?'$/i.test(part)
      );

      const scoreIndex = partsWithoutMinute.findIndex((part: string) =>
        /\b\d+\s*-\s*\d+\b/.test(part)
      );
      const scoreMatch =
        scoreIndex >= 0
          ? partsWithoutMinute[scoreIndex].match(/(\d+)\s*-\s*(\d+)/)
          : undefined;
      const score =
        scoreMatch && scoreMatch[1] != null && scoreMatch[2] != null
          ? `${scoreMatch[1]}-${scoreMatch[2]}`
          : undefined;

      const cleanedParts =
        scoreIndex >= 0
          ? partsWithoutMinute.filter(
              (_part: string, idx: number) => idx !== scoreIndex
            )
          : partsWithoutMinute;

      const title =
        cleanedParts.length > 0
          ? cleanedParts.join(" - ")
          : rawLabel.replace(/^\d+(?:\+\d+)?'\s*-\s*/i, "").trim();

      const mentionedTeam = detectTeam(
        `${rawLabel} ${title}`,
        homeNames,
        awayNames
      );

      if (!mentionedTeam) return null;

      const minuteText = periodLabelFromText(rawLabel);
      const type = deriveTypeFromText(lowerLabel);

      const identifier = item?.id ?? `${index}`;

      return {
        id: String(identifier),
        type,
        team: mentionedTeam,
        minute,
        extraMinutes: extra,
        minuteText,
        title: title || undefined,
        subtitle: score,
        raw: rawLabel,
      };
    })
    .filter(Boolean) as MatchTimelineEvent[];

  return events.reverse();
};

type IceHockeyTimelineProps = {
  events: MatchTimelineEvent[];
  status: MatchStatus;
};

const IceHockeyMatchTimeline: React.FC<IceHockeyTimelineProps> = ({
  events,
  status,
}) => {
  const { home, away } = getTeamColors(status as any);

  const markers = useMemo(() => {
    if (!events.length) return { top: undefined, bottom: undefined };
    const marker = {
      type: "whistle",
      iconKey: "whistle",
    } as MatchTimelineEvent;
    return { top: marker, bottom: marker };
  }, [events.length]);

  if (!events.length) return null;

  return (
    <MatchTimeline
      events={events}
      homeColor={home}
      awayColor={away}
      title="Match Timeline"
      topMarker={markers.top}
      bottomMarker={markers.bottom}
    />
  );
};

export default IceHockeyMatchTimeline;
